<template>
    <div>
        <el-drawer title="Add & Assign New User" :visible.sync="showDrawer" size="35%" direction="rtl"
            @closed="handleClose">
            <div class="drawer-content">
                <p class="text-muted">Create a new field user and assign them to selected areas in one step.</p>
                <hr>

                <div class="form-group">
                    <label>Areas *</label>
                    <el-select v-model="selectedAreas" multiple filterable placeholder="Select Areas"
                        no-data-text="No areas available" style="width: 100%;">
                        <el-option v-for="area in projectAreas" :key="area.id" :label="area.name" :value="area.id">
                        </el-option>
                    </el-select>
                </div>

                <div class="row">
                    <div class="col-12">
                        <label>Username *</label>
                        <fg-input v-model="username" type="text" :disabled="isSubmitting"
                            placeholder="Enter username (letters, numbers, dots, underscores)"></fg-input>
                    </div>
                </div>

                <div class="row">
                    <div class="col-6">
                        <label>First Name</label>
                        <fg-input v-model="firstName" type="text" :disabled="isSubmitting"
                            placeholder="Enter first name"></fg-input>
                    </div>
                    <div class="col-6">
                        <label>Last Name</label>
                        <fg-input v-model="lastName" type="text" :disabled="isSubmitting"
                            placeholder="Enter last name"></fg-input>
                    </div>
                </div>

                <div class="row">
                    <div class="col-6">
                        <label>Password *</label>
                        <fg-input v-model="password" type="password" :disabled="isSubmitting"
                            placeholder="Min 6 characters"></fg-input>
                    </div>
                    <div class="col-6">
                        <label>Confirm Password *</label>
                        <fg-input v-model="passwordConfirmation" type="password" :disabled="isSubmitting"
                            placeholder="Confirm password"></fg-input>
                    </div>
                </div>

                <div class="form-group">
                    <label>Role *</label>
                    <div>
                        <p-radio inline v-for="role in roles" :key="role.value"
                            v-model="selectedRole" :value="role.value" :label="role.value">
                            {{ role.label }}
                        </p-radio>
                    </div>
                </div>

                <p class="text-muted"><small>* If the username already exists, the existing user will be assigned to the selected areas. Password will remain unchanged for existing users.</small></p>

                <div class="text-right mt-3">
                    <el-button :loading="isSubmitting" v-on:click="submitForm" type="primary" round>
                        Add & Assign User
                    </el-button>
                </div>
            </div>
        </el-drawer>
    </div>
</template>

<script>
import Swal from 'sweetalert2';
import { Select, Option, Drawer, Button } from 'element-ui';

export default {
    components: {
        [Option.name]: Option,
        [Select.name]: Select,
        [Drawer.name]: Drawer,
        [Button.name]: Button,
    },
    props: {
        show: {
            type: Boolean,
            default: false,
        },
        projectId: {
            type: Number,
            required: true,
        },
        projectAreas: {
            type: Array,
            default: () => [],
        },
        roles: {
            type: Array,
            required: true,
            // Each item: { value: 'vaccination', label: 'Vaccination' }
        },
    },
    data() {
        return {
            showDrawer: false,
            selectedAreas: [],
            username: '',
            firstName: '',
            lastName: '',
            password: '',
            passwordConfirmation: '',
            selectedRole: '',
            isSubmitting: false,
        };
    },
    watch: {
        show(val) {
            this.showDrawer = val;
            if (val && this.roles.length > 0 && !this.selectedRole) {
                this.selectedRole = this.roles[0].value;
            }
        },
    },
    methods: {
        handleClose() {
            this.$emit('close');
        },
        resetForm() {
            this.selectedAreas = [];
            this.username = '';
            this.firstName = '';
            this.lastName = '';
            this.password = '';
            this.passwordConfirmation = '';
            this.selectedRole = this.roles.length > 0 ? this.roles[0].value : '';
        },
        submitForm() {
            let vm = this;

            if (vm.selectedAreas.length === 0) {
                vm.showAlert('Error', 'Please select at least one area', false);
                return;
            }
            if (!vm.username || vm.username.trim() === '') {
                vm.showAlert('Error', 'Please enter a username', false);
                return;
            }
            if (!/^[A-Za-z0-9._]+$/.test(vm.username.trim())) {
                vm.showAlert('Error', 'Username can only contain letters, numbers, dots, and underscores', false);
                return;
            }
            if (!vm.password || vm.password.length < 6) {
                vm.showAlert('Error', 'Password must be at least 6 characters', false);
                return;
            }
            if (vm.password !== vm.passwordConfirmation) {
                vm.showAlert('Error', 'Passwords do not match', false);
                return;
            }
            if (!vm.selectedRole) {
                vm.showAlert('Error', 'Please select a role', false);
                return;
            }

            new Swal({
                icon: 'info',
                title: 'Are you sure?',
                text: 'You want to add this user and assign them to the selected areas!',
                type: 'warning',
                showCancelButton: true,
                confirmButtonClass: 'btn btn-success btn-fill',
                cancelButtonClass: 'btn btn-danger btn-fill',
                confirmButtonText: 'Yes, Add & Assign!',
                buttonsStyling: false,
            }).then(function (result) {
                if (result.value) {
                    vm.isSubmitting = true;
                    const loading = vm.$loading({
                        lock: true,
                        text: 'Adding & Assigning...',
                        spinner: 'el-icon-loading',
                        background: 'rgba(0, 0, 0, 0.7)',
                    });

                    axios.post('area/assign/user/' + vm.selectedRole + '/' + vm.projectId, {
                        username: vm.username.trim(),
                        password: vm.password,
                        first_name: vm.firstName,
                        last_name: vm.lastName,
                        area_ids: vm.selectedAreas,
                    }).then(response => {
                        loading.close();
                        vm.isSubmitting = false;
                        if (response.data.response.error.error_code === 0) {
                            vm.showAlert('Success', response.data.response.error.msg, true);
                            vm.resetForm();
                            vm.handleClose();
                            vm.$emit('assigned');
                        } else {
                            vm.showAlert('Error', response.data.response.error.error_msg, false);
                        }
                    }).catch(error => {
                        loading.close();
                        vm.isSubmitting = false;
                        if (error.response && error.response.data && error.response.data.response) {
                            vm.showAlert('Error', error.response.data.response.error.error_msg, false);
                        } else {
                            vm.showAlert('Error', 'Something went wrong. Please try again.', false);
                        }
                    });
                }
            });
        },
        showAlert(title, message, isSuccess) {
            new Swal({
                title: title,
                text: message,
                icon: isSuccess ? 'success' : 'error',
                confirmButtonClass: 'btn btn-info btn-fill',
                buttonsStyling: false,
            });
        },
    },
    mounted() {
        this.showDrawer = this.show;
        if (this.roles.length > 0) {
            this.selectedRole = this.roles[0].value;
        }
    },
};
</script>

<style scoped>
.drawer-content {
    padding: 0 20px 20px;
}

.drawer-content label {
    font-weight: 600;
    margin-bottom: 5px;
    display: block;
}

.drawer-content .form-group {
    margin-bottom: 15px;
}

.drawer-content .row {
    margin-bottom: 10px;
}
</style>
